import * as React from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { useSession } from 'next-auth/react'

import { RequestFieldsEnum } from '@/enums/RequestFieldsEnum'
import { getQueryCacheKey } from '@/helpers/getQueryCacheKey'
import { getUserId } from '@/helpers/getUserId'
import { useSelectedOrgUnitId } from '@/hooks/useSelectedOrgUnitId'
import { useSiteContext } from '@/hooks/useSiteContext'
import { productsClient } from '@/lib/clients/productsClient'

import type { Product } from '@/gen/ecom'

export const useFullProductQuery = () => {
  const { getBaseSiteId } = useSiteContext()
  const { data: session } = useSession()
  const selectedOrgUnitId = useSelectedOrgUnitId()
  const baseSiteId = getBaseSiteId()
  const userId = getUserId(session)

  return React.useCallback(
    (productCode: string) => {
      const request = { baseSiteId, productCode, fields: RequestFieldsEnum.Full }

      return {
        queryKey: getQueryCacheKey(productsClient.getProduct.name, { ...request, userId }, selectedOrgUnitId),
        queryFn: () =>
          productsClient.getProduct(request, {
            headers: { 'Content-Type': 'application/json', 'sap-commerce-cloud-user-id': userId },
          }),
      }
    },
    [baseSiteId, userId, selectedOrgUnitId]
  )
}

export const useLoadFullProduct = () => {
  const queryClient = useQueryClient()
  const getFullProductQuery = useFullProductQuery()

  return React.useCallback(
    async (productCode: string): Promise<Product | null> => {
      try {
        return await queryClient.fetchQuery({
          ...getFullProductQuery(productCode),
          staleTime: Number.POSITIVE_INFINITY,
        })
      } catch {
        return null
      }
    },
    [queryClient, getFullProductQuery]
  )
}
